import { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { api } from '../../services/api'
import { ChevronLeft, Activity } from 'lucide-react'
import { toast } from 'sonner'

interface Log {
  id: string
  action: string
  details: any
  ipAddress: string
  createdAt: string
  admin: { name: string; email: string }
}

const actionLabels: Record<string, string> = {
  LOGIN: 'Login realizado',
  LOGOUT: 'Logout realizado',
  UPDATE_STAGE: 'Atualização de etapa',
  UPDATE_SETTINGS: 'Atualização de configurações'
}

export default function LogsList() {
  const { token } = useAuth()
  const [logs, setLogs] = useState<Log[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    loadLogs()
  }, [token, page])

  const loadLogs = async () => {
    if (!token) return
    setIsLoading(true)
    try {
      const data = await api.getAdminLogs(token, page)
      setLogs(data.logs)
      setTotal(data.total)
    } catch (error) {
      toast.error('Erro ao carregar logs')
    } finally {
      setIsLoading(false)
    }
  }

  const totalPages = Math.ceil(total / 50)

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">Carregando...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4 sticky top-0 z-10">
        <div className="flex items-center gap-4">
          <a href="/admin/dashboard" className="text-gray-400 hover:text-gray-600">
            <ChevronLeft size={20} />
          </a>
          <div>
            <h1 className="text-xl font-serif font-bold text-[#0d2d47]">Logs do Sistema</h1>
            <p className="text-sm text-gray-500">Histórico de ações dos administradores</p>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Data/Hora</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Administrador</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Ação</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Detalhes</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                      {new Date(log.createdAt).toLocaleString('pt-BR')}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{log.admin.name}</div>
                      <div className="text-xs text-gray-400">{log.admin.email}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-600">
                        <Activity size={10} />
                        {actionLabels[log.action] || log.action}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {log.details ? JSON.stringify(log.details) : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-400 font-mono text-xs">
                      {log.ipAddress || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 text-sm text-gray-600 disabled:opacity-50"
              >
                Anterior
              </button>
              <span className="text-sm text-gray-500">
                Página {page} de {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 text-sm text-gray-600 disabled:opacity-50"
              >
                Próxima
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}